<template>
  <Modal
    v-model:visible="isVisible"
    class="shipment-financials-cost-allocation-modal"
    :header="t('costAllocation.costAllocation')"
    :dismissable-mask="false"
    size="lg"
  >
    <ShipmentRateCostAllocation
      ref="shipmentRateCostAllocationRef"
      v-model="tableSettings"
      :shipment="shipment"
      :shipment-financials="shipmentFinancials"
      :has-read-only-permission="isReadOnly"
      @discrepancy-cost-allocation="hasAllocationDiscrepancy = $event"
    />

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        :disabled="loading"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :label="t('common.save')"
        :loading="loading"
        :disabled="isSaveButtonDisabled"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  PropType,
  ref,
  Ref,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { Module } from '@rygen/rygen-ui/model';
import { CostAllocationTableSettings } from '@rygen/rygen-ui/model/costAllocation';
import { ShipmentFinancials } from '@rygen/rygen-ui/model/shipmentFinancials';

import { Shipment } from '@/model';

import ShipmentRateCostAllocation from '@/components/shipmentFinancials/costAllocation/ShipmentRateCostAllocation.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: false,
});

const props = defineProps({
  shipment: {
    type: Object as PropType<Shipment>,
    default: () => new Shipment(),
  },
  shipmentFinancials: {
    type: Object as PropType<ShipmentFinancials>,
    default: () => new ShipmentFinancials(),
  },
});
const { shipment, shipmentFinancials } = toRefs(props);

const shipmentRateCostAllocationRef = useTemplateRef(
  'shipmentRateCostAllocationRef',
);

const { t } = useI18n();

const tableSettings: Ref<CostAllocationTableSettings> = ref(
  new CostAllocationTableSettings({ module: Module.TMS }),
);
const loading = ref(false);
const hasAllocationDiscrepancy = ref(false);

const isReadOnly = computed(
  () => !authorization.hasAuthority('shipment', 'updateCostAllocation'),
);

const isSaveButtonDisabled = computed(
  () =>
    tableSettings.value.readOnly ||
    loading.value ||
    hasAllocationDiscrepancy.value ||
    isReadOnly.value,
);

const onCancel = () => {
  isVisible.value = false;
};

const onSave = async () => {
  try {
    loading.value = true;

    const success =
      await shipmentRateCostAllocationRef.value?.saveRateCostAllocation();

    if (success) {
      isVisible.value = false;
    }
  } finally {
    loading.value = false;
  }
};

watch(isVisible, async (newVal: boolean) => {
  if (newVal) {
    await nextTick(() => {
      shipmentRateCostAllocationRef.value?.getRateCostAllocation();
    });
  }
});
</script>
